/**
 * A Finding is the unit of output. Not a metric, not a log line: a specific,
 * scoped, evidenced claim that something is wrong.
 *
 * The core field names (`code`, `severity`, `message`, `lotId`, `fix`) are the
 * same ones `GET /sales/:saleId/integrity` has returned since M4, deliberately.
 * That route's own comment anticipates this ADR - "sale-day findings that need
 * ring state ... join here once the ring event drain lands" (briefs.ts:126) -
 * so an IntegrityFinding lifts into a Finding by adding scope and evidence,
 * rather than the two growing into parallel vocabularies that no screen can
 * merge.
 *
 * `evidence` is the part that earns its keep. A finding saying "the projector
 * is behind" carries the two numbers that were subtracted and where they were
 * read. That is what makes a finding arguable in the moment, and what makes an
 * incident snapshot worth anything six months later.
 */

export const SEVERITY = ['error', 'warning', 'info'] as const;
export type Severity = (typeof SEVERITY)[number];

/** Serializable evidence values. Anything richer belongs in a linked snapshot. */
export type EvidenceValue = string | number | boolean | null;
export type EvidenceBag = Readonly<Record<string, EvidenceValue>>;

/**
 * Where a finding was found. Every level is optional because findings exist at
 * every level: a dependency probe is platform-wide, an invariant is usually
 * lot-shaped. `tenantSlug` rather than `tenantId` because the operator reads it.
 */
/**
 * Optionals here are written `?: T | undefined` rather than `?: T`. Under
 * `exactOptionalPropertyTypes` those differ, and the second one does not accept
 * what `z.optional()` infers - so the schema mirror in schema.ts would not be
 * assignable to the type it mirrors, which is the one thing that file exists to
 * prove.
 */
export interface Scope {
  readonly tenantSlug?: string | undefined;
  readonly saleId?: string | undefined;
  /** The ENGINE's ring id (rings.engine_ring_id), not the rings.id uuid. */
  readonly ringId?: string | undefined;
  readonly lotId?: string | undefined;
}

export interface Finding {
  /** Stable, greppable, SCREAMING_SNAKE. Part of the contract; do not rename. */
  readonly code: string;
  readonly severity: Severity;
  /** One sentence, for a human, naming the specific thing. */
  readonly message: string;
  readonly scope: Scope;
  readonly evidence: EvidenceBag;
  /** Names a runbook that could address this, when one exists. Never executed here. */
  readonly runbook?: string | undefined;
  /** IntegrityFinding compatibility: a confirmable tenant action that fixes it. */
  readonly fix?: { readonly action: string; readonly args: Readonly<Record<string, unknown>> } | undefined;
}

const ORDER: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

/** Errors first, then warnings, then info; stable within a severity. */
export const bySeverity = (findings: readonly Finding[]): readonly Finding[] =>
  [...findings].sort((a, b) => ORDER[a.severity] - ORDER[b.severity]);

export interface FindingSummary {
  readonly errors: number;
  readonly warnings: number;
  readonly info: number;
}

export function summarize(findings: readonly Finding[]): FindingSummary {
  let errors = 0, warnings = 0, info = 0;
  for (const f of findings) {
    if (f.severity === 'error') errors++;
    else if (f.severity === 'warning') warnings++;
    else info++;
  }
  return { errors, warnings, info };
}

/**
 * A finding's identity for deduplication across repeated runs. Deliberately
 * excludes `message` and `evidence`: the same broken thing observed twice with
 * different numbers is one incident, not two. Excluding evidence is also what
 * keeps a flapping value from minting an incident per poll.
 */
export const fingerprint = (f: Finding): string =>
  [f.code, f.scope.tenantSlug ?? '', f.scope.saleId ?? '', f.scope.ringId ?? '', f.scope.lotId ?? ''].join('|');
